import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'student' or 'university'
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedStudent = localStorage.getItem('student');
      const storedUniversity = localStorage.getItem('university');

      if (storedToken) {
        setToken(storedToken);
        
        if (storedStudent) {
          try {
            setUser(JSON.parse(storedStudent));
            setUserType('student');
          } catch (error) {
            console.error('Error parsing student data:', error);
            clearAuth();
          }
        } else if (storedUniversity) {
          try {
            setUser(JSON.parse(storedUniversity));
            setUserType('university');
          } catch (error) {
            console.error('Error parsing university data:', error);
            clearAuth();
          }
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (userData, type, authToken) => {
    setUser(userData);
    setUserType(type);
    setToken(authToken);
    
    localStorage.setItem('token', authToken);
    localStorage.setItem(type, JSON.stringify(userData));
  };

  const logout = () => {
    clearAuth();
  };

  const clearAuth = () => {
    setUser(null);
    setUserType(null);
    setToken(null);
    
    localStorage.removeItem('token');
    localStorage.removeItem('student');
    localStorage.removeItem('university');
    localStorage.removeItem('requirePasswordChange');
  };

  const updateUser = (updatedData) => {
    setUser(updatedData);
    if (userType) {
      localStorage.setItem(userType, JSON.stringify(updatedData));
    }
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isStudent = () => {
    return userType === 'student';
  };

  const isUniversity = () => {
    return userType === 'university';
  };

  const value = {
    user,
    userType,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    isStudent,
    isUniversity
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
